import type { GameAction, GameEvent, PlayerId, PlayerObservationV1 } from "@fate-kingdom/engine";

export type AlphaPresentationKind =
  "turn-start" | "draw" | "play-card" | "disband" | "event" | "attack" | "cleanup" | "turn-handoff";

export interface AlphaPresentationTransaction {
  readonly actionSequence: number;
  readonly actorPlayerId: PlayerId;
  readonly action: GameAction;
  readonly events: readonly GameEvent[];
  readonly beforeObservation: PlayerObservationV1;
  readonly afterObservation: PlayerObservationV1;
  readonly presentationKind: AlphaPresentationKind;
}

export function alphaPresentationKind(action: GameAction): AlphaPresentationKind {
  switch (action.type) {
    case "found-land":
    case "develop-land":
    case "deploy-commander":
    case "replace-commander":
    case "deploy-army":
      return "play-card";
    case "disband-army":
    case "disband-commander":
    case "discard-army-for-upkeep":
      return "disband";
    case "play-event":
      return "event";
    case "attack":
      return "attack";
    case "end-turn":
      return "cleanup";
    case "advance-phase":
      return action.playerId === "player-1" || action.playerId === "player-2"
        ? "turn-handoff"
        : "turn-start";
    default:
      return "turn-start";
  }
}

const NORMAL_DURATION_BY_KIND: Readonly<Record<AlphaPresentationKind, number>> = {
  "turn-start": 320,
  draw: 500,
  "play-card": 540,
  disband: 480,
  event: 650,
  attack: 900,
  cleanup: 680,
  "turn-handoff": 360,
};

export function alphaPresentationDuration(
  transaction: Pick<AlphaPresentationTransaction, "presentationKind" | "events">,
  options: { readonly quick: boolean; readonly reducedMotion: boolean },
): number {
  const normal =
    transaction.presentationKind === "cleanup"
      ? Math.min(
          1_800,
          NORMAL_DURATION_BY_KIND.cleanup +
            transaction.events.reduce((duration, event) => {
              if (event.type === "cleanup-hand-discarded") {
                return duration + event.cardInstanceIds.length * 70;
              }
              if (event.type === "cards-drawn") {
                return duration + event.cardInstanceIds.length * 110;
              }
              return event.type === "deck-recycled" ? duration + 180 : duration;
            }, 0),
        )
      : NORMAL_DURATION_BY_KIND[transaction.presentationKind];
  if (options.reducedMotion) return Math.min(160, Math.round(normal * 0.2));
  if (options.quick) return Math.min(220, Math.round(normal * 0.3));
  return normal;
}
